/**
 * serviceUtils.jsx
 * ----------------
 * Shared utilities for ServiceCard and ServiceDetailCard.
 * Import from here — never define these in individual components.
 */

import { useState } from "react";
import { ChevronDown } from "./icons";

/**
 * Returns background + text colour for a process step actor badge.
 * Driven entirely by the actor string from service_registry.py steps[].actor.
 */
export function actorStyle(actor = "") {
  const map = {
    "You":  { bg: "#dbeafe", color: "#1e40af" },
    "BPC":  { bg: "#dcfce7", color: "#15803d" },
    "TD":   { bg: "#fef3c7", color: "#92400e" },
    "DCSD": { bg: "#ede9fe", color: "#5b21b6" },
  };
  return map[actor] || { bg: "var(--color-background-secondary)", color: "var(--color-text-secondary)" };
}

/**
 * Collapsible section with animated chevron.
 * Used in both ServiceCard and ServiceDetailCard.
 */
export function Collapsible({ label, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: "0.5px solid var(--color-border-tertiary)", marginTop: "2px" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center",
          justifyContent: "space-between", background: "none",
          border: "none", cursor: "pointer", padding: "9px 0",
          fontSize: "12px", fontWeight: "500",
          color: "var(--color-text-primary)",
          fontFamily: "var(--font-sans)",
        }}
      >
        <span>{label}</span>
        <span style={{
          transition: "transform 0.2s",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          color: "var(--color-text-secondary)",
        }}>
          <ChevronDown />
        </span>
      </button>
      {open && <div style={{ paddingBottom: "10px" }}>{children}</div>}
    </div>
  );
}

/**
 * Shared TAT table renderer.
 * Accepts service.tat[] from the backend and renders Urban/Rural columns.
 * Used in both ServiceCard and ServiceDetailCard.
 */
export function TatTable({ tat }) {
  if (!tat || tat.length === 0) return null;
  const multiTat = tat.length > 1;

  return (
    <div style={{
      background: "var(--color-background-secondary)",
      border: "0.5px solid var(--color-border-tertiary)",
      borderRadius: "var(--border-radius-md)",
      overflow: "hidden",
    }}>
      {/* Column headers */}
      <div style={{
        display: "grid",
        gridTemplateColumns: multiTat ? "1fr 100px 100px" : "1fr 110px",
        padding: "6px 10px",
        borderBottom: "0.5px solid var(--color-border-tertiary)",
      }}>
        <span style={{ fontSize: "10px", color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {multiTat ? "Customer type" : "Location"}
        </span>
        <span style={{ fontSize: "10px", color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "center" }}>
          Urban
        </span>
        {multiTat && (
          <span style={{ fontSize: "10px", color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "center" }}>
            Rural
          </span>
        )}
      </div>

      {tat.map((row, i) => (
        <div key={i}>
          <div style={{
            display: "grid",
            gridTemplateColumns: multiTat ? "1fr 100px 100px" : "1fr 110px",
            padding: "7px 10px", alignItems: "center",
            borderBottom: i < tat.length - 1 ? "0.5px solid var(--color-border-tertiary)" : "none",
          }}>
            <span style={{ fontSize: "12px", color: "var(--color-text-secondary)" }}>
              {multiTat ? row.type : "Urban"}
            </span>
            <span style={{ fontSize: "12px", fontWeight: "500", color: "#15803d", textAlign: "center" }}>
              {row.urban}
            </span>
            {multiTat
              ? <span style={{ fontSize: "12px", fontWeight: "500", color: "var(--color-text-primary)", textAlign: "center" }}>{row.rural}</span>
              : <span style={{ fontSize: "11px", color: "var(--color-text-secondary)", textAlign: "right" }}>Rural: {row.rural}</span>
            }
          </div>
          {row.note && (
            <div style={{ padding: "0 10px 7px", fontSize: "10px", color: "var(--color-text-tertiary)", fontStyle: "italic" }}>
              {row.note}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Shared steps list renderer.
 * Accepts service.steps[] from the backend and renders actor-tagged timeline.
 * Used in both ServiceCard and ServiceDetailCard.
 */
export function StepsList({ steps }) {
  if (!steps || steps.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
      {steps.map((step, i) => {
        const ac = actorStyle(step.actor);
        return (
          <div key={i} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", flexShrink: 0, minWidth: "44px" }}>
              <span style={{ fontSize: "9px", fontWeight: "500", color: "var(--color-text-tertiary)", textAlign: "center", lineHeight: 1 }}>
                {step.day}
              </span>
              <span style={{ fontSize: "9px", fontWeight: "500", padding: "1px 5px", borderRadius: "3px", background: ac.bg, color: ac.color, whiteSpace: "nowrap" }}>
                {step.actor}
              </span>
            </div>
            <div style={{
              flex: 1, fontSize: "12px", color: "var(--color-text-secondary)",
              lineHeight: 1.45, paddingTop: "2px",
              borderLeft: "2px solid var(--color-border-tertiary)",
              paddingLeft: "8px",
            }}>
              {step.text}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Shared docs list renderer.
 * Accepts service.required_docs[] from the backend.
 */
export function DocsList({ docs }) {
  if (!docs || docs.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
      {docs.map((doc, i) => (
        <div key={i} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
          <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#16a34a", marginTop: "5px", flexShrink: 0 }} />
          <span style={{ fontSize: "12px", color: "var(--color-text-secondary)", lineHeight: 1.45 }}>{doc}</span>
        </div>
      ))}
      <p style={{ margin: "5px 0 0", fontSize: "10px", color: "var(--color-text-tertiary)" }}>
        Max file size: 0.5 MB per document
      </p>
    </div>
  );
}